// The engineer's SAFed test job on the phone (Session BDA; rebuilt from LG1 Annex A in Session BDU). JOB-SPECIFIC and PHONE FIRST: the engineer
// sees only the tests raised on THIS job for THIS lift, never the catalogue. The flow is one gate screen, then one test at a time, then
// restoration where it applies, then submit.
//
// A test screen is the printed Annex A form in printed order (the server sends its items): yes / no questions as big buttons (A.10's three also
// offer N/A), measurements with their printed units, free text and date prompts, and the two closing items every form prints: "Does the
// result ... indicate further remedial work is necessary?" and, when Yes, the recommendation. The LG1 reference behind the "Method" expander
// is a pointer, the numbers LG1 states and the recommended periodicity, never a paragraph of LG1. "Stress test" is ELE's own risk label, and
// says so. The gate's LR1 section is a set of pointers to LR1 Issue 04 by section number.
//
// The server is the authority: it refuses a start without the signed authorisation, two engineers and a recorded evidence route, and
// refuses a submit until the gate, every test and restoration are complete. The buttons here only tell the engineer first. The whole
// record rides the completion payload (safedRecord), so a sheet finished with no signal arrives whole when the outbox drains.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronLeft } from "lucide-react";
import { api, ApiError, type SafedAnnexItemView, type SafedJobView, type SafedRecordView, type SafedTestAnswerView, type SafedTestView } from "../../lib/api";
import { adoptDraft, gateItemDone, labelWithUnit } from "../../lib/safedRecordDraft";
import { storageKeySuffix } from "../../lib/phonePreview";
import JobTypeBadge from "../../components/JobTypeBadge";
import { useEngineerData } from "./EngineerDataContext";
import { SafedPhotos } from "./SafedPhotos";
import { SafedHowItWorksBody } from "./SafedHowItWorks";

// Inside the Phone view the draft is kept under its own key (Session BDZ), so it never appears in the real app.
const DRAFT_KEY = (jobId: string) => `safed:record:${jobId}${storageKeySuffix()}`;

function readDraft(jobId: string): SafedRecordView | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY(jobId));
    return raw ? (JSON.parse(raw) as SafedRecordView) : null;
  } catch {
    return null;
  }
}
function writeDraft(jobId: string, record: SafedRecordView): void {
  try {
    window.localStorage.setItem(DRAFT_KEY(jobId), JSON.stringify(record));
  } catch {
    /* best effort: the draft can never break the sheet */
  }
}
function clearDraft(jobId: string): void {
  try {
    window.localStorage.removeItem(DRAFT_KEY(jobId));
  } catch {
    /* best effort */
  }
}

const btn = "min-h-12 w-full rounded-xl border px-4 py-2.5 font-heading text-base tracking-wide transition-colors";
const primary = `${btn} border-ele-green bg-ele-green text-ele-bg disabled:opacity-40`;
const quiet = `${btn} border-ele-border text-ele-strong`;
const inputCls = "mt-1 min-h-12 w-full rounded-ele border border-ele-border bg-ele-card px-3 text-ele-strong";

function StressBanner({ children }: { children: string }) {
  return (
    <div role="note" className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 p-3">
      <p className="flex items-center gap-2 font-heading text-sm uppercase tracking-wide text-amber-400">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Stress test (ELE risk label)
      </p>
      <p className="mt-1 text-sm text-ele-strong">{children}</p>
    </div>
  );
}

/** The reference behind a "Method" expander: where to read it, the numbers LG1 states and its recommended periodicity. No LG1 prose. */
function Method({ test }: { test: SafedTestView }) {
  const [open, setOpen] = useState(false);
  const r = test.reference;
  return (
    <div className="mb-4">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex min-h-11 items-center gap-1 font-menu text-xs uppercase tracking-wide text-ele-muted">
        Method <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="mt-1 space-y-2 rounded-ele border border-ele-border bg-ele-card p-3 text-sm text-ele-body">
          <p><span className="font-semibold text-ele-strong">Read.</span> {r.pointer}. Reference clause {test.clause}.</p>
          {r.oemFirst && <p><span className="font-semibold text-ele-strong">OEM manual first.</span> Follow the lift maker's instructions for this test.</p>}
          {r.parameters.length > 0 && (
            <div>
              <p className="font-semibold text-ele-strong">Numbers LG1 states</p>
              <ul className="list-disc pl-5">{r.parameters.map((p) => <li key={p}>{p}</li>)}</ul>
            </div>
          )}
          <p>
            <span className="font-semibold text-ele-strong">LG1 recommended periodicity.</span> {r.periodicity}
            {r.appliesWhere ? `, where ${r.appliesWhere}` : ""}. Guidance only, at the Competent Person's discretion.
          </p>
        </div>
      )}
    </div>
  );
}

/** A form from an older server (no `form`): the checks and values as plain items, so the sheet still works. */
function itemsFor(t: SafedTestView): SafedAnnexItemView[] {
  if (t.form?.items) return t.form.items;
  return [
    ...t.checks.map((c): SafedAnnexItemView => ({ kind: "yes_no", text: c, yesIsSatisfactory: true, sheet: 0 })),
    ...t.values.map((v): SafedAnnexItemView => ({ kind: v.unit ? "number" : "text", text: v.label, unit: v.unit || undefined, sheet: 0 })),
  ];
}

const isCheck = (i: SafedAnnexItemView) => i.kind === "yes_no" || i.kind === "yes_na_no";
const isValue = (i: SafedAnnexItemView) => i.kind === "number" || i.kind === "text" || i.kind === "choice" || i.kind === "date";

export function SafedTestSheet({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const { complete, online } = useEngineerData();
  const [dto, setDto] = useState<SafedJobView | null>(null);
  const [record, setRecord] = useState<SafedRecordView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // step: -1 gate, 0..n-1 a test, n restoration, n+1 submit
  const [step, setStep] = useState(-1);
  const [startReasons, setStartReasons] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failing, setFailing] = useState(false);
  const [facts, setFacts] = useState("");
  const [failureSteps, setFailureSteps] = useState<string[] | null>(null);
  const [submitReasons, setSubmitReasons] = useState<string[]>([]);
  // The LR1 items the engineer is writing a "not needed" reason for.
  const [excusing, setExcusing] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      const d = await api.safedJobs.get(jobId);
      setDto(d);
      // A local draft (typed offline) wins over the server copy until it has been submitted. One from an earlier build is adopted onto it.
      const draft = readDraft(jobId);
      setRecord(draft ? adoptDraft(draft, d.record, d.tests, d.gateItems.length) : d.record);
      setLoadError(null);
    } catch (err) {
      const draft = readDraft(jobId);
      if (draft) setRecord(draft);
      setLoadError(err instanceof ApiError ? "Could not load this test job. Try again when you have signal." : "No signal. Showing what is saved on this phone.");
    }
  }, [jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const update = (next: SafedRecordView) => {
    setRecord(next);
    writeDraft(jobId, next);
  };

  const tests = dto?.tests ?? [];
  const restorationStep = tests.length;
  const submitStep = tests.length + 1;
  const hasRestoration = dto ? record !== null && (tests.some((t) => t.restoration && record.tests[t.id]?.status !== "not_done")) : false;

  // Session BDB: what stops a stress test starting, shown BEFORE the engineer presses Start. The evidence route is the engineer's own
  // to choose on this screen, so it is not listed as a blocker here.
  const gateBlockers = useMemo(() => (dto ? dto.startRefusals.filter((r) => !r.includes("evidence route")) : []), [dto]);

  // The named gate list (an older server sends only the texts and the stress-only positions).
  const defs = useMemo(
    () =>
      dto
        ? (dto.gateDefs ?? dto.gateItems.map((text, i) => ({ id: `item_${i}`, text, group: "ELE" as const, kind: "tick" as const, stressOnly: dto.gateStressOnly.includes(i) })))
        : [],
    [dto],
  );

  const gateTicked = useMemo(() => {
    if (!dto || !record) return false;
    return defs.every((d, i) => gateItemDone(record, d, i, dto.stress));
  }, [dto, record, defs]);

  if (!record || !dto) {
    return (
      <div className="pb-8">
        <Link to="/engineer" className="inline-flex min-h-11 items-center gap-1 text-sm text-ele-muted"><ChevronLeft className="h-4 w-4" /> Back</Link>
        <p className="mt-4 text-ele-body" role={loadError ? "alert" : "status"}>{loadError ?? "Loading the test job..."}</p>
      </div>
    );
  }

  const setGateTick = (i: number, v: boolean) => {
    const ticks = [...record.gate.ticks];
    while (ticks.length < defs.length) ticks.push(false);
    ticks[i] = v;
    update({ ...record, gate: { ...record.gate, ticks } });
  };
  const setNotNeeded = (id: string, reason: string) => {
    const notNeeded = { ...(record.gate.notNeeded ?? {}) };
    if (reason.trim()) notNeeded[id] = reason;
    else delete notNeeded[id];
    update({ ...record, gate: { ...record.gate, notNeeded } });
  };
  const setAnswer = (testId: string, patch: Partial<SafedTestAnswerView>) => {
    update({ ...record, tests: { ...record.tests, [testId]: { ...record.tests[testId], ...patch } } });
  };

  async function startJob() {
    setBusy(true);
    setStartReasons([]);
    try {
      await api.safedJobs.saveRecord(jobId, record!);
      await api.safedJobs.start(jobId);
      setStep(0);
      await load();
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { reasons?: string[]; message?: string } | null) : null;
      setStartReasons(body?.reasons?.length ? body.reasons : [body?.message ?? "Could not start. Check your signal and try again."]);
    } finally {
      setBusy(false);
    }
  }

  async function reportFailure(testId: string) {
    setBusy(true);
    try {
      const res = await api.safedJobs.failure(jobId, { testId, facts });
      setFailureSteps(res.steps);
      clearDraft(jobId); // the stopped test is the server's act: a stale local draft must not reopen it
      setFailing(false);
      await load();
    } catch {
      setFailureSteps(dto?.failureSteps ?? []);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setSubmitReasons([]);
    try {
      // Online, the server judges the record first and its reasons are shown here, so a refusal is never a silent queue.
      if (online) {
        try {
          const fresh = await api.safedJobs.saveRecord(jobId, record!);
          if (fresh.startRefusals.length > 0 || fresh.submitRefusals.length > 0) {
            setSubmitReasons([...fresh.startRefusals, ...fresh.submitRefusals]);
            return;
          }
        } catch (err) {
          const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
          if (body?.message) {
            setSubmitReasons([body.message]);
            return;
          }
        }
      }
      // Offline it queues and is judged on arrival.
      await complete(jobId, { checklist: [], defects: [], partsUsed: [], safedRecord: record, completedAt: new Date().toISOString() } as never);
      clearDraft(jobId);
      navigate("/engineer", { replace: true, state: { submitted: { ref: dto?.assetRef ?? "SAFed test", site: dto?.siteName ?? "", online } } });
    } catch {
      setSubmitReasons(["Could not submit. Your answers are saved on this phone."]);
    } finally {
      setBusy(false);
    }
  }

  const header = (
    <header className="mb-4">
      <Link to="/engineer" className="inline-flex min-h-11 items-center gap-1 text-sm text-ele-muted"><ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back</Link>
      <div className="mt-2"><JobTypeBadge type="safed_test" /></div>
      <h1 className="mt-2 font-heading text-[26px] font-bold leading-tight text-ele-strong">{dto.assetRef ?? "Lift"}</h1>
      <p className="text-sm text-ele-muted">{dto.siteName}</p>
      {loadError && <p role="alert" className="mt-2 text-sm text-amber-400">{loadError}</p>}
    </header>
  );

  // ---- Gate screen --------------------------------------------------------------------------------------------------
  if (step === -1) {
    const textField = (label: string, key: keyof SafedRecordView["gate"]) => (
      <label className="mt-3 block text-sm text-ele-body">
        {label}
        <input
          className={inputCls}
          value={String(record.gate[key] ?? "")}
          onChange={(e) => update({ ...record, gate: { ...record.gate, [key]: e.target.value } })}
        />
      </label>
    );
    const eleDefs = defs.map((d, i) => ({ d, i })).filter((x) => x.d.group === "ELE");
    const accessDef = defs.map((d, i) => ({ d, i })).find((x) => x.d.kind === "access");
    const lr1Defs = defs.map((d, i) => ({ d, i })).filter((x) => x.d.kind === "lr1");
    const accessNo = record.gate.accessNeeded === "no" || record.gate.preLr1 === true;
    return (
      <div className="pb-28">
        {header}
        {dto.stress && <StressBanner>A component can fail under test. No signature, no attendance. Two engineers. Nobody works alone on a stress test.</StressBanner>}
        <details className="mb-4 rounded-ele border border-ele-border bg-ele-card p-3">
          <summary className="min-h-11 cursor-pointer py-2 font-heading text-base text-ele-strong">How a SAFed day works</summary>
          <div className="mt-2"><SafedHowItWorksBody /></div>
        </details>
        {dto.stress && gateBlockers.length > 0 && (
          <div role="alert" className="mb-4 rounded-ele border border-ele-danger p-3">
            <p className="font-heading text-ele-danger">This stress test cannot start yet</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-ele-strong">
              {gateBlockers.map((r) => <li key={r}>{r}</li>)}
            </ul>
          </div>
        )}
        <h2 className="font-heading text-lg text-ele-strong">Before you start</h2>
        <p className="text-sm text-ele-muted">Tests on this job: {tests.map((t) => t.id).join(", ")}</p>
        <ul className="mt-3 space-y-2">
          {eleDefs.map(({ d, i }) => {
            const na = !dto.stress && d.stressOnly;
            return (
              <li key={d.id} className="rounded-ele border border-ele-border bg-ele-card p-3">
                <label className="flex items-start gap-3 text-sm text-ele-body">
                  <input type="checkbox" className="mt-1 h-6 w-6 shrink-0" checked={na || record.gate.ticks[i] === true} disabled={na} onChange={(e) => setGateTick(i, e.target.checked)} />
                  <span>{d.text}{na ? " (not needed for this job)" : ""}</span>
                </label>
              </li>
            );
          })}
        </ul>

        {accessDef && (
          <section className="mt-5" aria-labelledby="lr1-heading">
            <h2 id="lr1-heading" className="font-heading text-lg text-ele-strong">Access, LR1 Issue 04</h2>
            <p className="text-sm text-ele-muted">Pointers to LR1 Issue 04 by section number. Tick each one that applies, or say why it is not needed for this test.</p>
            <fieldset className="mt-3">
              <legend className="text-sm text-ele-strong">{accessDef.d.text}</legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(["yes", "no"] as const).map((a) => (
                  <button
                    key={a}
                    type="button"
                    aria-pressed={record.gate.accessNeeded === a}
                    onClick={() => update({ ...record, gate: { ...record.gate, accessNeeded: a } })}
                    className={`min-h-12 rounded-xl border font-heading text-lg ${record.gate.accessNeeded === a ? "border-ele-green bg-ele-green text-ele-bg" : "border-ele-border text-ele-strong"}`}
                  >
                    {a === "yes" ? "Yes" : "No"}
                  </button>
                ))}
              </div>
            </fieldset>
            <ul className="mt-3 space-y-2">
              {lr1Defs.map(({ d, i }) => {
                const reason = (record.gate.notNeeded ?? {})[d.id] ?? "";
                const excusingThis = excusing[d.id] === true || reason.length > 0;
                return (
                  <li key={d.id} className="rounded-ele border border-ele-border bg-ele-card p-3">
                    <label className="flex items-start gap-3 text-sm text-ele-body">
                      <input type="checkbox" className="mt-1 h-6 w-6 shrink-0" checked={accessNo || record.gate.ticks[i] === true} disabled={accessNo} onChange={(e) => setGateTick(i, e.target.checked)} />
                      <span>{d.text}{accessNo ? " (not needed: no car top or pit access)" : ""}</span>
                    </label>
                    {!accessNo && record.gate.accessNeeded === "yes" && record.gate.ticks[i] !== true && (
                      <div className="mt-2">
                        {!excusingThis ? (
                          <button type="button" className="min-h-11 text-sm text-ele-muted underline" onClick={() => setExcusing({ ...excusing, [d.id]: true })}>
                            Not needed for this test
                          </button>
                        ) : (
                          <input
                            aria-label="Why this is not needed for this test"
                            placeholder="Not needed for this test because..."
                            className="min-h-12 w-full rounded-ele border border-ele-border bg-ele-bg px-3 text-ele-strong"
                            value={reason}
                            onChange={(e) => setNotNeeded(d.id, e.target.value)}
                          />
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="mt-2 text-sm text-ele-muted">If you find a car top control station that does not comply, or switches with no shroud, write it down as a fact in the comments on the test. State what you saw, not a conclusion.</p>
          </section>
        )}

        <fieldset className="mt-4">
          <legend className="text-sm text-ele-body">Which route are you testing to?</legend>
          {(["reduced", "standard"] as const).map((r) => (
            <label key={r} className="mt-2 flex min-h-12 items-center gap-3 rounded-ele border border-ele-border bg-ele-card px-3 text-ele-strong">
              <input type="radio" name="route" className="h-5 w-5" checked={record.gate.route === r} onChange={() => update({ ...record, gate: { ...record.gate, route: r } })} />
              {r === "reduced" ? "Reduced test (evidence found)" : "Test to the standard at installation (no evidence)"}
            </label>
          ))}
        </fieldset>
        {textField("Evidence found (document, date, who)", "evidenceFound")}
        {textField("OEM document and page", "oemDocument")}
        {textField("Quote or estimate reference", "quoteRef")}
        {textField("Engineer 1", "engineer1")}
        {textField("Engineer 2", "engineer2")}
        {textField("Test weights total", "weightsTotal")}
        {textField("Test weights supplier", "weightsSupplier")}
        {textField("Building contact", "buildingContact")}

        {!dto.authorisation && dto.stress && <p className="mt-4 text-sm text-amber-400">No signed authorisation is recorded. The office records it before a stress test can start.</p>}
        {startReasons.length > 0 && (
          <ul role="alert" className="mt-4 list-disc space-y-1 pl-5 text-sm text-ele-danger">
            {startReasons.map((r) => <li key={r}>{r}</li>)}
          </ul>
        )}
        <div className="mt-5 space-y-2">
          <button type="button" className={primary} disabled={busy || !gateTicked || !online} onClick={() => void startJob()}>
            {dto.status === "in_progress" ? "Continue to the first test" : "Start the test"}
          </button>
          {dto.status === "in_progress" && <button type="button" className={quiet} onClick={() => setStep(0)}>Go to the tests</button>}
          {!online && <p className="text-sm text-ele-muted">Starting needs a signal, so the office can confirm the authorisation. You can fill the sheet in offline once it has started.</p>}
        </div>
      </div>
    );
  }

  // ---- One test per screen: the printed Annex A form ----------------------------------------------------------------
  if (step >= 0 && step < tests.length) {
    const t = tests[step];
    const a = record.tests[t.id];
    const stopped = a.status === "stopped";
    const items = itemsFor(t);
    // The index of each item among the form's yes/no items and among its field items: the answer's arrays line up with them.
    let ci = 0;
    let vi = 0;
    const idx = items.map((it) => ({ c: isCheck(it) ? ci++ : -1, v: isValue(it) ? vi++ : -1 }));
    const sheets = t.form?.sheets ?? [{ heading: null, page: 0 }];
    const remedialQuestion = dto.annex?.remedialQuestion ?? "Does the result of the supplementary test(s) indicate further remedial work is necessary?";
    const recommendationPrompt = dto.annex?.recommendationPrompt ?? "If Yes, state your recommendation(s) for further action required:";

    const setCheck = (n: number, value: "yes" | "no" | "na" | null) => {
      const checks = [...a.checks];
      checks[n] = value;
      setAnswer(t.id, { checks });
    };
    const noteFor = (n: number) => a.checkNotes[n] ?? "";
    const setNote = (n: number, v: string) => {
      const checkNotes = [...a.checkNotes];
      while (checkNotes.length <= n) checkNotes.push("");
      checkNotes[n] = v;
      setAnswer(t.id, { checkNotes });
    };
    const setValue = (n: number, v: string) => {
      const values = [...a.values];
      while (values.length <= n) values.push("");
      values[n] = v;
      setAnswer(t.id, { values });
    };

    const renderItem = (it: SafedAnnexItemView, k: number) => {
      const { c, v } = idx[k]!;
      if (it.kind === "heading") return <h3 key={k} className="mb-2 mt-4 font-heading text-base text-ele-strong">{it.text}</h3>;
      if (it.kind === "note") return <p key={k} className="mb-3 text-sm text-ele-muted">{it.text}</p>;
      if (it.kind === "explain") return null; // answered by the note under the answer that needs one
      if (isCheck(it)) {
        const answer = a.checks[c] ?? null;
        const options = it.kind === "yes_na_no" ? (["yes", "na", "no"] as const) : (["yes", "no"] as const);
        const needsNote = (it.yesIsSatisfactory === false ? answer === "yes" : answer === "no") || answer === "na";
        const notePlaceholder = answer === "na" ? "Describe in detail what was inspected, the condition, the probability of successful operation and why it could not be operated" : it.yesIsSatisfactory === false ? "A Yes needs its explanation" : "A No needs a note";
        return (
          <div key={k} className="mb-3 rounded-ele border border-ele-border bg-ele-card p-3">
            <p className="text-sm text-ele-strong">{it.text}</p>
            <div className={`mt-2 grid gap-2 ${options.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
              {options.map((ans) => {
                const bad = it.yesIsSatisfactory === false ? ans === "yes" : ans === "no";
                return (
                  <button
                    key={ans}
                    type="button"
                    aria-pressed={answer === ans}
                    onClick={() => setCheck(c, answer === ans && it.conditional ? null : ans)}
                    className={`min-h-14 rounded-xl border font-heading text-lg ${answer === ans ? (bad ? "border-ele-danger bg-ele-danger text-ele-white" : "border-ele-green bg-ele-green text-ele-bg") : "border-ele-border text-ele-strong"}`}
                  >
                    {ans === "yes" ? "Yes" : ans === "no" ? "No" : "N/A"}
                  </button>
                );
              })}
            </div>
            {it.conditional && <p className="mt-1 text-xs text-ele-muted">Complete as applicable: tap the answer again to leave it blank if it does not apply.</p>}
            {it.footnote && it.kind === "yes_na_no" && <p className="mt-1 text-xs text-ele-muted">* {it.footnote}</p>}
            {needsNote && (
              <input aria-label="Note for this answer" placeholder={notePlaceholder} className="mt-2 min-h-12 w-full rounded-ele border border-ele-border bg-ele-bg px-3 text-ele-strong" value={noteFor(c)} onChange={(e) => setNote(c, e.target.value)} />
            )}
          </div>
        );
      }
      if (it.kind === "choice") {
        return (
          <fieldset key={k} className="mb-3">
            <legend className="text-sm text-ele-body">{it.text}</legend>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {(it.options ?? []).map((o) => (
                <button key={o} type="button" aria-pressed={(a.values[v] ?? "") === o} onClick={() => setValue(v, o)} className={`min-h-12 rounded-xl border font-heading ${(a.values[v] ?? "") === o ? "border-ele-green bg-ele-green text-ele-bg" : "border-ele-border text-ele-strong"}`}>
                  {o}
                </button>
              ))}
            </div>
          </fieldset>
        );
      }
      return (
        <label key={k} className="mb-3 block text-sm text-ele-body">
          {labelWithUnit(it.text, it.unit)}
          <input
            type={it.kind === "date" ? "date" : "text"}
            inputMode={it.kind === "number" ? "decimal" : undefined}
            className={inputCls}
            value={a.values[v] ?? ""}
            onChange={(e) => setValue(v, e.target.value)}
          />
        </label>
      );
    };

    return (
      <div className="pb-28">
        {header}
        <p className="font-menu text-xs uppercase tracking-wide text-ele-muted">Test {step + 1} of {tests.length}</p>
        <h2 className="mt-1 font-heading text-xl text-ele-strong">{t.id} {t.printedTitle ?? t.name}</h2>
        <p className="text-xs text-ele-muted">Reference clause {t.clause}</p>
        {t.cls === "STRESS" && <div className="mt-3"><StressBanner>This test loads a safety device to its limit. If it fails, stop. Do not re-run it.</StressBanner></div>}
        <Method test={t} />

        {stopped ? (
          <div role="alert" className="rounded-ele border border-ele-danger p-3">
            <p className="font-heading text-ele-danger">Test stopped</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-ele-strong">
              {(failureSteps ?? dto.failureSteps).map((s) => <li key={s}>{s}</li>)}
            </ol>
          </div>
        ) : (
          <>
            {sheets.map((sheet, sheetIndex) => (
              <section key={sheetIndex}>
                {sheetIndex > 0 && <h3 className="mb-2 mt-5 border-t border-ele-border pt-3 font-heading text-base text-ele-strong">{sheet.heading}</h3>}
                {items.map((it, k) => (it.sheet === sheetIndex ? renderItem(it, k) : null))}
              </section>
            ))}

            <section className="mt-5 rounded-ele border border-ele-border bg-ele-card p-3" aria-label="Closing items">
              <p className="text-sm text-ele-strong">{remedialQuestion}</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(["yes", "no"] as const).map((ans) => (
                  <button key={ans} type="button" aria-pressed={a.remedial === ans} onClick={() => setAnswer(t.id, { remedial: ans })} className={`min-h-14 rounded-xl border font-heading text-lg ${a.remedial === ans ? "border-ele-green bg-ele-green text-ele-bg" : "border-ele-border text-ele-strong"}`}>
                    {ans === "yes" ? "Yes" : "No"}
                  </button>
                ))}
              </div>
              {a.remedial === "yes" && (
                <label className="mt-3 block text-sm text-ele-body">
                  {recommendationPrompt}
                  <textarea className="mt-1 min-h-20 w-full rounded-ele border border-ele-border bg-ele-bg p-3 text-ele-strong" value={a.recommendation ?? ""} onChange={(e) => setAnswer(t.id, { recommendation: e.target.value })} />
                </label>
              )}
              <p className="mt-2 text-xs text-ele-muted">Further remedial work is a recorded result, not a failed test.</p>
            </section>

            <label className="mb-3 mt-4 block text-sm text-ele-body">
              Further comments
              <textarea className="mt-1 min-h-20 w-full rounded-ele border border-ele-border bg-ele-card p-3 text-ele-strong" value={a.comment} onChange={(e) => setAnswer(t.id, { comment: e.target.value })} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm text-ele-body">
                Tested by
                <input className={inputCls} value={a.testedBy} onChange={(e) => setAnswer(t.id, { testedBy: e.target.value })} />
              </label>
              <label className="block text-sm text-ele-body">
                Date
                <input type="date" className={inputCls} value={a.testedOn} onChange={(e) => setAnswer(t.id, { testedOn: e.target.value })} />
              </label>
            </div>
            <div className={`mt-4 grid gap-2 ${t.cls === "STRESS" ? "grid-cols-2" : "grid-cols-3"}`} role="group" aria-label="Result of this test">
              {([["passed", "Passed"], ["failed", "Failed"], ["not_done", "Not done"]] as const)
                .filter(([val]) => !(val === "failed" && t.cls === "STRESS"))
                .map(([val, label]) => (
                <button key={val} type="button" aria-pressed={a.status === val} onClick={() => setAnswer(t.id, { status: val })} className={`min-h-12 rounded-xl border font-heading ${a.status === val ? "border-ele-green bg-ele-green text-ele-bg" : "border-ele-border text-ele-strong"}`}>
                  {label}
                </button>
              ))}
            </div>
            {t.cls === "STRESS" && !failing && (
              <button type="button" onClick={() => setFailing(true)} className="mt-4 min-h-14 w-full rounded-xl border-2 border-ele-danger font-heading text-lg text-ele-danger">
                Something did not work
              </button>
            )}
            {failing && (
              <div className="mt-4 rounded-ele border border-ele-danger p-3">
                <p className="font-heading text-ele-danger">Stop. Do not re-run the test. Make safe and phone the Director.</p>
                <p className="mt-1 text-sm text-ele-body">If you can, photograph the damage first (Add photo, below). Photos are optional and never stop you recording this.</p>
                <label className="mt-2 block text-sm text-ele-body">
                  What happened (facts only, no cause or blame)
                  <textarea className="mt-1 min-h-24 w-full rounded-ele border border-ele-border bg-ele-card p-3 text-ele-strong" value={facts} onChange={(e) => setFacts(e.target.value)} />
                </label>
                <button type="button" disabled={busy || facts.trim().length < 3 || !online} onClick={() => void reportFailure(t.id)} className={`${primary} mt-3 border-ele-danger bg-ele-danger text-ele-white`}>
                  Record this and stop
                </button>
                {!online && <p className="mt-2 text-sm text-ele-muted">Needs a signal so the office is told at once. Make safe first.</p>}
              </div>
            )}
          </>
        )}

        <SafedPhotos jobId={jobId} prompts={t.photoPrompts ?? []} />

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button type="button" className={quiet} onClick={() => setStep(step - 1)}>Back</button>
          <button type="button" className={primary} onClick={() => setStep(step + 1)}>Next</button>
        </div>
      </div>
    );
  }

  // ---- Restoration (Part R, an ELE rule) ----------------------------------------------------------------------------
  if (step === restorationStep) {
    if (!hasRestoration) {
      return (
        <div className="pb-28">
          {header}
          <p className="text-ele-body">No restoration is needed for the tests on this job.</p>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <button type="button" className={quiet} onClick={() => setStep(step - 1)}>Back</button>
            <button type="button" className={primary} onClick={() => setStep(submitStep)}>Next</button>
          </div>
        </div>
      );
    }
    return (
      <div className="pb-28">
        {header}
        <h2 className="font-heading text-lg text-ele-strong">Restoration</h2>
        <p className="text-sm text-ele-muted">ELE rule. LG1 asks that no deterioration is confirmed after the test and friction components are replaced if necessary.</p>
        <ul className="mt-3 space-y-2">
          {dto.restorationItems.map((item, i) => (
            <li key={item} className="rounded-ele border border-ele-border bg-ele-card p-3">
              <label className="flex items-start gap-3 text-sm text-ele-body">
                <input
                  type="checkbox"
                  className="mt-1 h-6 w-6 shrink-0"
                  checked={record.restoration.ticks[i] === true}
                  onChange={(e) => {
                    const ticks = [...record.restoration.ticks];
                    ticks[i] = e.target.checked;
                    update({ ...record, restoration: { ...record.restoration, ticks } });
                  }}
                />
                <span>{item}</span>
              </label>
            </li>
          ))}
        </ul>
        <label className="mt-3 block text-sm text-ele-body">
          Parts fitted
          <textarea className="mt-1 min-h-20 w-full rounded-ele border border-ele-border bg-ele-card p-3 text-ele-strong" value={record.restoration.parts} onChange={(e) => update({ ...record, restoration: { ...record.restoration, parts: e.target.value } })} />
        </label>
        <SafedPhotos jobId={jobId} prompts={["Safety gear before and after the test", "Guide rail surface after restoration"]} />
        <div className="mt-5 grid grid-cols-2 gap-2">
          <button type="button" className={quiet} onClick={() => setStep(step - 1)}>Back</button>
          <button type="button" className={primary} onClick={() => setStep(submitStep)}>Next</button>
        </div>
      </div>
    );
  }

  // ---- Submit -------------------------------------------------------------------------------------------------------
  const witness = record.competentWitness ?? { name: "", organisation: "", date: "" };
  const setWitness = (patch: Partial<typeof witness>) => update({ ...record, competentWitness: { ...witness, ...patch } });
  return (
    <div className="pb-28">
      {header}
      <h2 className="font-heading text-lg text-ele-strong">Submit for approval</h2>
      <ul className="mt-3 space-y-1 text-sm text-ele-body">
        {tests.map((t) => (
          <li key={t.id}>{t.id} {t.name}: <span className="font-semibold text-ele-strong">{{ pending: "Not recorded", passed: "Passed", failed: "Failed", stopped: "Stopped", not_done: "Not done" }[record.tests[t.id].status]}</span></li>
        ))}
      </ul>
      <fieldset className="mt-5 rounded-ele border border-ele-border bg-ele-card p-3">
        <legend className="px-1 text-sm text-ele-strong">Witnessed by (Competent Person), if there was one</legend>
        <p className="text-xs text-ele-muted">Only if a Competent Person watched the tests. They are not one of our engineers. Leave it blank if nobody did.</p>
        <label className="mt-2 block text-sm text-ele-body">
          Name
          <input className={inputCls} value={witness.name} onChange={(e) => setWitness({ name: e.target.value })} />
        </label>
        <label className="mt-2 block text-sm text-ele-body">
          Organisation (kept on our file, not printed)
          <input className={inputCls} value={witness.organisation} onChange={(e) => setWitness({ organisation: e.target.value })} />
        </label>
        <label className="mt-2 block text-sm text-ele-body">
          Date
          <input type="date" className={inputCls} value={witness.date} onChange={(e) => setWitness({ date: e.target.value })} />
        </label>
      </fieldset>
      {submitReasons.length > 0 && (
        <ul role="alert" className="mt-4 list-disc space-y-1 pl-5 text-sm text-ele-danger">
          {submitReasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
      <div className="mt-5 grid grid-cols-2 gap-2">
        <button type="button" className={quiet} onClick={() => setStep(restorationStep)}>Back</button>
        <button type="button" className={primary} disabled={busy} onClick={() => void submit()}>Submit</button>
      </div>
      <p className="mt-3 text-sm text-ele-muted">With a signal, anything still missing is listed here before you submit. Without one, the sheet is kept on this phone and checked when it reaches the office.</p>
    </div>
  );
}
